import React from 'react';
import { AppAdminFormLabel, AppButton, AppMultiSelect } from '@/components/atoms';
import { Search, Download } from '@/components/atoms/AppIcon';
import { SearchTextFieldWithLabel } from '@/components/molecules';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { useParams } from 'react-router-dom';
type TenantOption = { name: string; site_type: string };

type MassagersFilterValues = {
  siteTypes: string[];
  clinicNumber: string;
  clinicName: string;
  searchLoginId: string;
  searchName: string;
};

type MassagersFilterPanelProps = {
  tenants?: TenantOption[];
  filters: MassagersFilterValues;
  onChangeFilters: (next: Partial<MassagersFilterValues>) => void;
  onSearch: () => void;
  onCsv: () => void;
  csvLoading?: boolean;
};

const MassagersFilterPanel: React.FC<MassagersFilterPanelProps> = ({
  tenants = [],
  filters,
  onChangeFilters,
  onSearch,
  onCsv,
  csvLoading = false,
}) => {
  const { colors } = useTheme();
  const { admin } = useSiteAdminAuth();
  const { site } = useParams<{ site: string }>();

  const showSiteFilter = (admin?.is_full_admin && site === 'super-admin') || site === 'medix';
  const tenantSiteTypes = tenants.map(t => t.site_type).filter(Boolean) as string[];
  const hasAllSelected =
    tenantSiteTypes.length > 0 && tenantSiteTypes.every(st => filters.siteTypes.includes(st));

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.stopPropagation();
    onSearch();
  };

  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      <div className="grid grid-cols-3 gap-4 flex-1">
        {showSiteFilter && (
          <div>
            <AppAdminFormLabel label="サイト区分" />
            <AppMultiSelect
              id="massager_site_types"
              placeholder="サイト区分"
              value={filters.siteTypes}
              className="w-full"
              maxTagCount={hasAllSelected ? 0 : 'responsive'}
              maxTagPlaceholder={hasAllSelected ? () => 'すべて' : undefined}
              showSearch
              options={[
                { label: 'すべて', value: 'all' },
                ...tenants.map(t => ({ label: t.name, value: t.site_type })),
              ]}
              onChange={values => {
                const next = (values as string[]) || [];
                if (next.includes('all')) {
                  onChangeFilters({ siteTypes: tenantSiteTypes });
                  return;
                }
                onChangeFilters({ siteTypes: next.filter(v => v !== 'all') });
              }}
              onInputKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            />
          </div>
        )}
        <SearchTextFieldWithLabel
          label="治療院ID"
          title="治療院ID"
          id="massager_clinic_number"
          value={filters.clinicNumber}
          placeholder="治療院ID"
          onChange={e => onChangeFilters({ clinicNumber: e.target.value })}
          onKeyDown={handleEnterSearch}
        />
        <SearchTextFieldWithLabel
          label="治療院名"
          id="massager_clinic_name"
          value={filters.clinicName}
          placeholder="治療院名"
          onChange={e => onChangeFilters({ clinicName: e.target.value })}
          onKeyDown={handleEnterSearch}
        />
        <SearchTextFieldWithLabel
          label="施術者ログインID"
          id="massager_search_login_id"
          value={filters.searchLoginId}
          placeholder="施術者ログインID"
          onChange={e => onChangeFilters({ searchLoginId: e.target.value })}
          onKeyDown={handleEnterSearch}
        />
        <SearchTextFieldWithLabel
          label="施術者名"
          id="massager_search_name"
          value={filters.searchName}
          placeholder="施術者名"
          onChange={e => onChangeFilters({ searchName: e.target.value })}
          onKeyDown={handleEnterSearch}
        />
        <div className="flex gap-2 items-center">
          <AppButton
            onClick={onSearch}
            type="primary"
            icon={<Search />}
            style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
          >
            検索
          </AppButton>
          <AppButton
            onClick={onCsv}
            type="primary"
            icon={<Download />}
            disabled={csvLoading}
            style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
          >
            {csvLoading ? '出力中...' : 'CSV'}
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default MassagersFilterPanel;
